/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { gql } from "graphql-tag";
import { ApolloError, useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { graphqlErrorIndicatesForbidden } from "utils/graphql-auth-error";
import { refetchHandles } from "data";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { getActivityFeed, getActivityFeed_activityFeed as ActivityFeedType } from "types/gql-types";
import Tabs from "components/tabs";
import ActivityItem from "./activity-item";

const FEED_QUERY = gql`
  query getActivityFeed($interestId: ID!) {
    activityFeed(resourceId: $interestId) {
      id
      name
      insertedAt
      title
      type
      note
      authorFullName
      message {
        status
        error {
          code
          message
        }
        recipientName
        emailToAddress
        emailCcAddress
        body
        mobileNumber
      }
      metadata {
        to
        emailSubject
        body
        viewerName
        updateDiff
        errorsMap
        reason
        owner
        ownerName
        previousOwner
        previousOwnerName
        activityContent
        filename
        uploadingUserEmail
        messageType
        otherInterests {
          id
          firstName
          lastName
        }
      }
      letterJob {
        lobLetterId
        status
        errorMessage
      }
    }
  }
`;

type FeedType = "all" | "updates" | "notes" | "messages" | "skips" | "phone";

const messagesExclude:(string | null)[] = [
  "note",
  "log_call",
  "skip_applicant",
];

type activityFeedTypeFilter = (act:ActivityFeedType) => boolean

const messagesOnlyFilter:activityFeedTypeFilter = (act) => act.name !== null &&
  (act.name.includes("email") ||
  act.name.includes("sms") ||
  act.name.includes("letter")
  );

const tabs = {
  all: {
    label: "All",
    filter: () => true,
  },
  updates: {
    label: "Updates",
    filter: (act:ActivityFeedType) =>
      !messagesExclude.includes(act.type) && !messagesOnlyFilter(act),
  },
  messages: {
    label: "Messages",
    filter: messagesOnlyFilter,
  },
  skips: {
    label: "Skips",
    filter: (act:ActivityFeedType) => act.type === "skip_applicant",
  },
  phone: {
    label: "Calls",
    filter: (act:ActivityFeedType) => act.type === "log_call",
  },
  notes: {
    label: "Notes",
    filter: (act:ActivityFeedType) => act.type === "note",
  },
} as Record<FeedType, {label: string, filter: (act:ActivityFeedType) => boolean}>;
const tabKeys = Object.keys(tabs) as FeedType[];

const filterActivityFeed = (activityFeed: ActivityFeedType[], feedName: FeedType) => {
  if (!activityFeed) return [];

  return activityFeed.filter(tabs[feedName].filter);
};

const showCount = (
  loading: boolean,
  error: ApolloError | undefined,
  data: getActivityFeed | undefined,
  feedName: FeedType,
) => {
  if (loading) return "";
  if (error || !data || !data.activityFeed) return "";

  return filterActivityFeed(data.activityFeed, feedName).length;
};

const ActivityFeed: React.FC<{ interestId: string }> = ({ interestId }) => {
  const [selectedFeed, setSelectedFeed] = useState<FeedType>("all");
  const { loading, error, data, refetch } =
    useQuery<getActivityFeed>(FEED_QUERY, { fetchPolicy: "network-only", variables: { interestId } });
  const handleClick = (feedName: FeedType) => setSelectedFeed(feedName);
  // allow other part of app to call this
  refetchHandles.activityFeed = refetch;

  return (
    <>
      <div style={{ marginLeft: -16, marginRight: -16, marginTop: -16 }}>
        <Tabs
          value={selectedFeed}
          setValue={(value) => handleClick(value)}
          tabs={tabKeys.map((value) => {
            const { label } = tabs[value];
            return {
              value,
              label,
              badgeContents: showCount(loading, error, data, value),
            };
          })}
        />
      </div>
      <Grid container spacing={2} direction="column" alignItems="center" justifyContent="center">
        {error && graphqlErrorIndicatesForbidden(error) && <Navigate to="/not-authorized" replace />}
        {error && !graphqlErrorIndicatesForbidden(error) && <ServerError desc={error.message} />}
        {loading && <LoadingSpinner />}
        {data && (
          <div data-cy="feed" style={{ width: "100%" }}>
            {filterActivityFeed(data.activityFeed, selectedFeed)
              .map((act: ActivityFeedType, idx: number) =>
                <ActivityItem key={idx} data={act} interestId={interestId} />)}
          </div>)}
      </Grid>
    </>
  );
};

export default ActivityFeed;
